/**
 * VH-287 — the admin adds a user. A set-up mail goes to the email: with its
 * code the person chooses a password and sets up the authenticator. No
 * password is typed here; the admin never knows it.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { adminAPI } from '../../services/api'

const ROLES = ['user', 'coach']
const LANGUAGES = ['en', 'de', 'pt-BR']
const ERRORS = {
  email_taken: 'admin.users.add.emailTaken',
  email_reserved: 'admin.users.add.emailReserved',
  invalid_email: 'admin.users.add.invalidEmail',
}
const EMPTY = { email: '', first_name: '', last_name: '', role: 'user', language: 'en' }

export default function AddUserForm({ onClose }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form, setForm] = useState(EMPTY)
  const [error, setError] = useState(null)

  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value })

  const add = useMutation({
    mutationFn: (user) => adminAPI.addUser(user),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      if (data.setup_mail.sent) toast.success(t('admin.users.add.added', { email: data.user.email }))
      else toast.error(t('admin.users.add.addedNoMail', { email: data.user.email }))
      setForm(EMPTY)
      onClose()
    },
    onError: (err) => {
      const code = err?.response?.data?.detail?.code
      setError(t(ERRORS[code] || 'admin.users.add.failed'))
    },
  })

  const submit = (event) => {
    event.preventDefault()
    if (!form.email.trim()) {
      setError(t('admin.users.add.invalidEmail'))
      return
    }
    setError(null)
    add.mutate({ ...form, email: form.email.trim() })
  }

  return (
    <form onSubmit={submit} noValidate className="mb-6 space-y-4 rounded-lg border border-gray-200 p-4">
      <p className="text-sm text-gray-600">{t('admin.users.add.intro')}</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="add-email" className="form-label">{t('admin.users.email')}</label>
          <input id="add-email" type="email" value={form.email} onChange={set('email')} className="form-input" />
        </div>
        <div>
          <label htmlFor="add-first" className="form-label">{t('admin.users.add.firstName')}</label>
          <input id="add-first" type="text" value={form.first_name} onChange={set('first_name')} className="form-input" />
        </div>
        <div>
          <label htmlFor="add-last" className="form-label">{t('admin.users.add.lastName')}</label>
          <input id="add-last" type="text" value={form.last_name} onChange={set('last_name')} className="form-input" />
        </div>
        <div>
          <label htmlFor="add-role" className="form-label">{t('admin.users.role')}</label>
          <select id="add-role" value={form.role} onChange={set('role')} className="form-input">
            {ROLES.map((role) => (
              <option key={role} value={role}>{t(`admin.users.roles.${role}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="add-language" className="form-label">{t('admin.users.add.language')}</label>
          <select id="add-language" value={form.language} onChange={set('language')} className="form-input">
            {LANGUAGES.map((language) => (
              <option key={language} value={language}>{t(`admin.users.add.languages.${language}`)}</option>
            ))}
          </select>
        </div>
      </div>
      {error && <p role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={onClose} className="btn-secondary">{t('common.cancel')}</button>
        <button type="submit" disabled={add.isPending} className="btn-primary">
          {add.isPending ? t('admin.users.add.adding') : t('admin.users.add.submit')}
        </button>
      </div>
    </form>
  )
}
